import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import Cookies from 'universal-cookie';

import { current } from '../services/api/users';
import { AuthToken } from '../entities/auth-token';
import { Customer } from '../entities/customer';
import { Order } from '../entities/order';
import { isSelfCo, getRegionFromUrl } from '../helpers/locale-helper';
import { COOKIE_NAME_ANSWER } from './questionnaire';

const COOKIE_NAME_TOKEN_ADMIN = 'admin_token';

function redirectToUserRegion(userRegion: 'eu' | 'uk' | 'us'): boolean {
  if (!isSelfCo()) return false;

  const urlRegion = getRegionFromUrl();

  if (urlRegion && urlRegion !== userRegion) {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const locale = pathSegments[1];
    const restOfPath = pathSegments.slice(2).join('/');

    window.location.href = `/${userRegion}/${locale}${restOfPath ? `/${restOfPath}` : ''}${window.location.search}`;

    return true;
  }

  return false;
}

const COOKIE_NAME_TOKEN = 'access_token';
const COOKIE_NAME_PUBLIC = 'is_public';
const cookies = new Cookies();

interface ComponentProps {
  children: React.ReactNode;
}

interface ContextValue {
  token: string;
  tokenAdmin: string;
  loading: boolean;
  user?: Customer;
  setUserToken: (authToken: AuthToken, publicState: boolean) => Promise<void>;
  updateOrder: (orderHash: string, fields: Partial<Order>) => void;
  signout: () => void;
  updateUserFeedbackStatus: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<ContextValue>({
  token: '',
  tokenAdmin: '',
  loading: true,
  setUserToken: async () => Promise.resolve(),
  updateOrder: () => null,
  updateUserFeedbackStatus: () => null,
  signout: () => null,
  refreshUser: async () => Promise.resolve(),
});

function AuthContextProvider({ children }: ComponentProps) {
  const tokenAdmin = cookies.get(COOKIE_NAME_TOKEN_ADMIN);
  const [token, setToken] = useState<string>(cookies.get(COOKIE_NAME_TOKEN));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<Customer>();

  const signout = useCallback(() => {
    setToken('');
    setUser(undefined);
    cookies.remove(COOKIE_NAME_TOKEN, { path: '/' });
    cookies.remove(COOKIE_NAME_PUBLIC, { path: '/' });
    cookies.remove(COOKIE_NAME_ANSWER, { path: '/' });
    sessionStorage.removeItem('shopify_order');
  }, []);

  const setUserToken = useCallback(
    async (authToken: AuthToken, publicState: boolean) => {
      cookies.set(COOKIE_NAME_TOKEN, authToken.token, {
        expires: authToken.expiredAt,
        path: '/',
      });

      cookies.set(COOKIE_NAME_PUBLIC, publicState ? 1 : 0, {
        expires: authToken.expiredAt,
        path: '/',
      });

      if (authToken.customer?.region && redirectToUserRegion(authToken.customer.region)) {
        return;
      }

      setToken(authToken.token);
      setIsLoading(true);

      try {
        const currUser = await current(authToken.token);

        setUser(currUser);
        setIsLoading(false);
      } catch (e) {
        setUser(undefined);
        setIsLoading(false);
      }
    },
    [setToken],
  );

  const updateOrder = useCallback(
    (orderHash: string, fields: Partial<Order>) => {
      if (user) {
        const order = user.orders.find((o) => o.hash === orderHash);

        if (order) {
          const index = user.orders.indexOf(order);

          user.orders[index] = { ...order, ...fields };
        }
      }
    },
    [user],
  );

  const updateUserFeedbackStatus = useCallback(() => {
    if (user) {
      user.providedFeedback = true;
    }
  }, [user]);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const currUser = await current(token);

        setUser(currUser);
      } catch (e) {
        // Silently fail - user data will be refetched on next page load
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchUser = async (currToken: string) => {
      try {
        const currUser = await current(currToken);

        setUser(currUser);
        setIsLoading(false);

        if (currUser.region) {
          redirectToUserRegion(currUser.region);
        }
      } catch (e) {
        setUser(undefined);
        setIsLoading(false);
      }
    };

    if (!user && token) {
      fetchUser(token);
    }

    if (!token) {
      setIsLoading(false);
    }
  }, [token, setUserToken, user]);

  const contextValue = useMemo(
    () => ({
      token,
      tokenAdmin,
      signout,
      user,
      setUserToken,
      updateOrder,
      updateUserFeedbackStatus,
      refreshUser,
      loading: isLoading,
    }),
    [token, tokenAdmin, user, updateOrder, signout, setUserToken, isLoading, updateUserFeedbackStatus, refreshUser],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthContextProvider };
